import clsx from "clsx";
import Link, { LinkProps } from "next/link";
import { IconContainer } from "nvs-design-system/dist/IconContainer";
import { Spinner } from "nvs-design-system/dist/Spinner";
import { ButtonHTMLAttributes, PropsWithChildren } from "react";

type ButtonProps = PropsWithChildren<
  ButtonHTMLAttributes<HTMLButtonElement>
> & {
  buttonType?: ButtonTypes;
  isLoading?: boolean;
};

export enum ButtonType {
  primary = "primary",
  secondary = "secondary",
  tertiary = "tertiary",
  disabled = "disabled", // this should only be used for disabled buttons
}

type ButtonTypes = keyof typeof ButtonType;

const MAP_BUTTON_TYPE_TO_STYLE = {
  [ButtonType.primary]: clsx(
    "disabled:text-green-500 disabled:border-green-500 disabled:bg-transparent disabled:pointer-events-none",
    "bg-green-500 text-gray-950 border-green-500",
    "betterhover:hover:border-[#92CC22] betterhover:hover:bg-[#92CC22]",
  ),
  [ButtonType.secondary]: clsx(
    "disabled:text-gray-700 disabled:border-gray-700 disabled:betterhover:hover:border-gray-70 disabled:pointer-events-none",
    "text-gray-50 border-gray-500",
    "betterhover:hover:border-white active:border-white",
  ),
  [ButtonType.tertiary]: clsx(
    "text-green-500 border-green-500 bg-transparent",
    "betterhover:hover:border-[#92CC22] betterhover:hover:text-[#92CC22]",
  ),
  [ButtonType.disabled]: clsx(
    "disabled:text-gray-700 disabled:border-gray-500 disabled:bg-gray-500 disabled:pointer-events-none",
  ),
};

function getButtonStyleByType(buttonType: ButtonTypes) {
  return MAP_BUTTON_TYPE_TO_STYLE[buttonType];
}

// NOTE: this is the latest and greatest button from designs

export function Button({
  isLoading = false,
  buttonType = ButtonType.secondary,
  className,
  ...props
}: PropsWithChildren<ButtonProps>) {
  return (
    <button
      className={clsx(
        "relative rounded-md w-full py-3 px-base border",
        { "pointer-events-none": isLoading },
        getButtonStyleByType(buttonType),
        className,
      )}
      {...props}
    >
      {isLoading && (
        <span className="absolute inset-0 flex items-center justify-center">
          <IconContainer>
            <Spinner />
          </IconContainer>
        </span>
      )}
      <span className={clsx(isLoading && "invisible")}>{props.children}</span>
    </button>
  );
}

type LinkButtonProps = PropsWithChildren<LinkProps> & {
  buttonType?: ButtonTypes;
  isLoading?: boolean;
  className?: string;
};

export function LinkButton({
  isLoading = false,
  buttonType = ButtonType.secondary,
  className,
  ...props
}: LinkButtonProps) {
  return (
    <Link
      className={clsx(
        "block relative rounded-md w-full py-3 px-base border text-center",
        { "pointer-events-none": isLoading },
        getButtonStyleByType(buttonType),
        className,
      )}
      {...props}
    >
      {props.children}
    </Link>
  );
}
